import { useState, DragEvent, ChangeEvent, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { CustomToast } from "./CustomToast";
import { FileIcon } from "../images";

type Props = {
  uploadedFiles: File[];
  onFileUpload: (files: File[]) => void;
  acceptedExtensions: string[];
  mimeTypes?: string[];
  maxFiles?: number;
  maxSizeMB?: number;
  isDisabled?: boolean;
  label?: string;
};

export const FileDropZone = ({
  uploadedFiles,
  onFileUpload,
  acceptedExtensions,
  mimeTypes,
  maxFiles = 5,
  maxSizeMB = 50,
  isDisabled = false,
  label = "Drag your files here",
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const acceptedTypes = mimeTypes ?? acceptedExtensions.map((ext) => `.${ext}`);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList: FileList) => {
    const newFiles = Array.from(fileList);

    if (uploadedFiles.length + newFiles.length > maxFiles) {
      toast.error(
        <CustomToast
          type="error"
          text={`Maximum of ${maxFiles} files allowed`}
          title="File Upload Failed"
        />,
      );
      return;
    }

    const validFiles: File[] = [];
    let isSameValidMime = false;
    let isSameDuplicate = false;
    let isSameValidExtension = false;
    let isSameMaxSize = false;

    newFiles.forEach((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase();
      const isValidExtension =
        extension && acceptedExtensions.includes(extension);
      const isValidMime = !mimeTypes || mimeTypes.includes(file.type);

      const isDuplicate = uploadedFiles.some(
        (f) => f.name === file.name && f.size === file.size,
      );

      if (isDuplicate) {
        if (!isSameDuplicate) {
          toast.error(
            <CustomToast
              type="error"
              text={`You have already added this file: ${file.name}.`}
              title="File Upload Failed"
            />,
          );
          isSameDuplicate = true;
        }
        return;
      }

      if (!isValidExtension || !isValidMime) {
        if (!isSameValidExtension && !isSameValidMime) {
          toast.error(
            <CustomToast
              type="error"
              text={`You have selected an unsupported file extension.
               Please select a .${acceptedExtensions.join(", .")} file and try again`}
              title="File Upload Failed"
            />,
          );
          isSameValidExtension = true;
          isSameValidMime = true;
        }
        return;
      }

      if (file.size > maxSizeBytes) {
        if (!isSameMaxSize) {
          toast.error(
            <CustomToast
              type="error"
              text={`${file.name}" exceeds the ${maxSizeMB}MB size limit`}
              title="File Upload Failed"
            />,
          );
          isSameMaxSize = true;
        }

        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      onFileUpload(validFiles);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isInputDisabled = uploadedFiles.length >= maxFiles || isDisabled;

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !isInputDisabled && fileInputRef.current?.click()}
      sx={{
        opacity: isInputDisabled ? 0.5 : 1,
        border: "2px dashed #DEE0E2",
        borderRadius: "8px",
        padding: "26px",
        textAlign: "center",
        cursor: isInputDisabled ? "auto" : "pointer",
        backgroundColor: isInputDisabled
          ? "#FCFCFC"
          : dragging
            ? "#f4f4f5"
            : "#FCFCFC",
        transition: "background-color 0.2s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      <FileIcon />
      <Typography variant="body1" color="textSecondary" fontWeight={500}>
        {label} (max: {maxFiles})
      </Typography>
      <Typography variant="body2" color="#1C3C6C" fontWeight={500}>
        or select from your computer
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={acceptedTypes.join(",")}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
    </Box>
  );
};
